import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { Skeleton } from "antd";
import { formatRelativeTime } from "@/shared/lib/formatRelativeTime";
import { cn } from "@/shared/lib/utils";
import type { ReviewQueueSummary } from "@/portals/admin/api/adminReviewQueueApi";

/** Admin home "Review queue" card (Module 6) — live via the admin socket's review_queue:changed. */
export function ReviewQueueCard({ summary }: { summary: ReviewQueueSummary | null }) {
  return (
    <section aria-label="Review queue" className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E4002B]/50">
            <Icon icon="lucide:inbox" className="h-4 w-4 !text-white" />
          </div>
          <h2 className="text-base font-extrabold tracking-tight text-[#0A2947]">Review queue</h2>
        </div>
        <Link to="/admin/review-queue" className="text-sm font-semibold text-[#E4002B] hover:underline">
          View queue
        </Link>
      </div>

      {!summary ? (
        <Skeleton active title={false} paragraph={{ rows: 2 }} className="!mt-4" />
      ) : (
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <div>
            <p className="text-xs text-[#9CA3AF]">Unclaimed</p>
            <p data-testid="queue-unclaimed" className="text-3xl font-bold text-[#111827]">{summary.unclaimedCount}</p>
          </div>
          <div>
            <p className="text-xs text-[#9CA3AF]">Overdue</p>
            <p
              data-testid="queue-overdue"
              className={cn("text-3xl font-bold", summary.overdueCount > 0 ? "text-[#E4002B]" : "text-[#111827]")}
            >
              {summary.overdueCount}
            </p>
          </div>
          <div className="self-end">
            <p data-testid="queue-oldest" className="text-sm text-[#4B5563]">
              {summary.oldestWaitingSince ? `Oldest waiting: ${formatRelativeTime(summary.oldestWaitingSince)}` : "Nothing waiting"}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
